import { useCallback } from 'react';
import { useQueryClient, QueryClient } from 'react-query';
import { getUser } from '@frontend/auth-helpers';
import { queryClient as sharedQueryClient } from '@frontend/query-client';
import { useLocationGroupStore } from '@frontend/scope';
import type {
  WetagAggregationStrategy,
  WetagQueriesResult,
  WetagQueryResponse,
  WetagQueryOptions,
  WetagResult,
  WetagInput,
  WetagBulkResult,
  WetagBulkQueryAggregationMode,
  WetagBulkQueryResponse,
} from './types';
import {
  useWetag,
  useWetagQuery,
  useWetagQueries,
  prefetchWetag,
  fetchWetagValue,
  getWetagFromCache,
  invalidateWetag,
} from './use-wetag';
import { useWetagBulk, useWetagBulkQuery } from './use-wetag-bulk';

function currentUserId(): string | undefined {
  const user = getUser();
  return user?.userID;
}

function useCurrentOrgId(): string {
  const orgId = useLocationGroupStore((state) => state.selectedOrgId);
  return orgId;
}

function getCurrentOrgId(): string | undefined {
  return useLocationGroupStore.getState().selectedOrgId;
}

function useCurrentLocationId(): string | undefined {
  const locationIds = useLocationGroupStore((state) => state.selectedLocationIds);
  return locationIds.length > 0 ? locationIds[0] : undefined;
}

function getCurrentLocationId(): string | undefined {
  const locationIds = useLocationGroupStore.getState().selectedLocationIds;
  return locationIds.length > 0 ? locationIds[0] : undefined;
}

type WetagHook<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: WetagQueryOptions
) => [boolean, boolean, WetagQueryResponse | undefined];

type WetagQueryHook<TTag extends string> = (input: WetagInput<TTag>, options?: WetagQueryOptions) => WetagResult;

type UsePrefetchHook<TTag extends string> = () => (
  input: WetagInput<TTag>,
  options?: WetagQueryOptions
) => Promise<void>;

type UseInvalidateHook<TTag extends string> = () => (input: WetagInput<TTag>) => Promise<void>;

type UseGetFromCacheHook<TTag extends string> = () => (input: WetagInput<TTag>) => boolean | undefined;

type UseWetagQueriesHook<TTag extends string> = (
  input: WetagInput<TTag>,
  entityIds: string[],
  options?: WetagQueryOptions,
  strategy?: WetagAggregationStrategy
) => WetagQueriesResult;

type PrefetchFnWithEntityId<TTag extends string> = (
  input: WetagInput<TTag>,
  entityId: string,
  options?: WetagQueryOptions & { queryClient?: QueryClient }
) => Promise<void>;

type InvalidateFnWithEntityId<TTag extends string> = (
  input: WetagInput<TTag>,
  entityId: string,
  options?: { queryClient?: QueryClient }
) => Promise<void>;

type GetFromCacheFnWithEntityId<TTag extends string> = (
  input: WetagInput<TTag>,
  entityId: string,
  options?: { queryClient?: QueryClient }
) => boolean | undefined;

type FetchValueFnWithEntityId<TTag extends string> = (
  input: WetagInput<TTag>,
  entityId: string,
  options?: WetagQueryOptions & { queryClient?: QueryClient }
) => Promise<boolean>;

type PrefetchFnWithGetter<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: WetagQueryOptions & { queryClient?: QueryClient }
) => Promise<void>;

type InvalidateFnWithGetter<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: { queryClient?: QueryClient }
) => Promise<void>;

type GetFromCacheFnWithGetter<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: { queryClient?: QueryClient }
) => boolean | undefined;

type FetchValueFnWithGetter<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: WetagQueryOptions & { queryClient?: QueryClient }
) => Promise<boolean>;

type WetagPropDrivenHooks<TTag extends string> = {
  WetagHook: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: WetagQueryOptions
  ) => [boolean, boolean, WetagQueryResponse | undefined];
  WetagQueryHook: (input: WetagInput<TTag>, entityId: string | undefined, options?: WetagQueryOptions) => WetagResult;
  UsePrefetchHook: () => (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: WetagQueryOptions
  ) => Promise<void>;
  UseInvalidateHook: () => (input: WetagInput<TTag>, entityId: string | undefined) => Promise<void>;
  UseGetFromCacheHook: () => (input: WetagInput<TTag>, entityId: string | undefined) => boolean | undefined;
  UseWetagQueriesHook: (
    input: WetagInput<TTag>,
    entityIds: string[],
    options?: WetagQueryOptions,
    strategy?: WetagAggregationStrategy
  ) => WetagQueriesResult;
  PrefetchFnWithEntityId: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: WetagQueryOptions & { queryClient?: QueryClient }
  ) => Promise<void>;
  InvalidateFnWithEntityId: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: { queryClient?: QueryClient }
  ) => Promise<void>;
  GetFromCacheFnWithEntityId: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: { queryClient?: QueryClient }
  ) => boolean | undefined;
  FetchValueFnWithEntityId: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: WetagQueryOptions & { queryClient?: QueryClient }
  ) => Promise<boolean>;
  PrefetchFnWithGetter: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: WetagQueryOptions & { queryClient?: QueryClient }
  ) => Promise<void>;
  InvalidateFnWithGetter: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: { queryClient?: QueryClient }
  ) => Promise<void>;
  GetFromCacheFnWithGetter: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: { queryClient?: QueryClient }
  ) => boolean | undefined;
  FetchValueFnWithGetter: (
    input: WetagInput<TTag>,
    entityId: string | undefined,
    options?: WetagQueryOptions & { queryClient?: QueryClient }
  ) => Promise<boolean>;
  UseWetagBulkHook: WetagBulkHookOverloads<TTag>;
  UseWetagBulkQueryHook: WetagBulkQueryHookOverloads<TTag>;
};

interface WetagBulkHookOverloads<TTag extends string> {
  /**
   * Queries one or more tags for the supplied entity IDs.
   *
   * @param tag A tag or tags to query.
   * @param inputs Entity IDs to query.
   * @param strategy How to aggregate multiple query results. Defaults to `ANY`.
   * @param options React Query options.
   */
  (tag: TTag | TTag[], inputs: string[], strategy?: WetagBulkQueryAggregationMode, options?: WetagQueryOptions): [
    boolean,
    boolean,
    WetagBulkQueryResponse | undefined
  ];
  /**
   * Queries the supplied query-builder inputs.
   *
   * @param tag Set to `false` when supplying `has()`, `hasEntity()`, or other query-builder inputs.
   * @param inputs Query-builder inputs to evaluate.
   * @param strategy How to aggregate multiple query results. Defaults to `ANY`.
   * @param options React Query options.
   */
  (
    tag: false,
    inputs: WetagInput<TTag | `disabled-${TTag}`>[],
    strategy?: WetagBulkQueryAggregationMode,
    options?: WetagQueryOptions
  ): [boolean, boolean, WetagBulkQueryResponse | undefined];
}
interface WetagBulkQueryHookOverloads<TTag extends string> {
  /**
   * Queries one or more tags for the supplied entity IDs.
   *
   * @param tag A tag or tags to query.
   * @param inputs Entity IDs to query.
   * @param strategy How to aggregate multiple query results. Defaults to `ANY`.
   * @param options React Query options.
   */
  (
    tag: TTag | TTag[],
    inputs: string[],
    strategy?: WetagBulkQueryAggregationMode,
    options?: WetagQueryOptions
  ): WetagBulkResult;
  /**
   * Queries the supplied query-builder inputs.
   *
   * @param tag Set to `false` when supplying `has()`, `hasEntity()`, or other query-builder inputs.
   * @param inputs Query-builder inputs to evaluate.
   * @param strategy How to aggregate multiple query results. Defaults to `ANY`.
   * @param options React Query options.
   */
  (
    tag: false,
    inputs: WetagInput<TTag | `disabled-${TTag}`>[],
    strategy?: WetagBulkQueryAggregationMode,
    options?: WetagQueryOptions
  ): WetagBulkResult;
}

interface NamespacedHooksBase<TTag extends string> {
  /** Returns a tuple that looks like `[isEnabled, isLoading, data]`. Wetags are async so ensure you've handled loading states appropriately. */
  useWetag: WetagHook<TTag>;
  useWetagQuery: WetagQueryHook<TTag>;
  usePrefetch: UsePrefetchHook<TTag>;
  useInvalidate: UseInvalidateHook<TTag>;
  useGetFromCache: UseGetFromCacheHook<TTag>;
  /**
   * Fetches a wetag for multiple entity IDs in parallel. Returns aggregated loading state and per-entity data.
   * @deprecated Use `useWetagBulkQuery` instead. This hook is being deprecated in favor of a more explicit bulk query hook that better reflects its purpose and usage.
   */
  useWetagQueries: UseWetagQueriesHook<TTag>;
}

export interface NamespacedHooks<TTag extends string = string> extends NamespacedHooksBase<TTag> {
  prefetch: PrefetchFnWithEntityId<TTag>;
  invalidate: InvalidateFnWithEntityId<TTag>;
  getFromCache: GetFromCacheFnWithEntityId<TTag>;
  fetchValue: FetchValueFnWithEntityId<TTag>;
}

export interface NamespacedHooksWithGetter<TTag extends string = string> extends NamespacedHooksBase<TTag> {
  prefetch: PrefetchFnWithGetter<TTag>;
  invalidate: InvalidateFnWithGetter<TTag>;
  getFromCache: GetFromCacheFnWithGetter<TTag>;
  fetchValue: FetchValueFnWithGetter<TTag>;
}

export interface NamespacedHooksForPropDriven<TTag extends string> {
  useWetag: WetagPropDrivenHooks<TTag>['WetagHook'];
  useWetagQuery: WetagPropDrivenHooks<TTag>['WetagQueryHook'];
  usePrefetch: WetagPropDrivenHooks<TTag>['UsePrefetchHook'];
  useInvalidate: WetagPropDrivenHooks<TTag>['UseInvalidateHook'];
  useGetFromCache: WetagPropDrivenHooks<TTag>['UseGetFromCacheHook'];

  /**
   * @deprecated Use `useWetagBulkQuery` instead. This hook is being deprecated in favor of a more explicit bulk query hook that better reflects its purpose and usage.
   */
  useWetagQueries: WetagPropDrivenHooks<TTag>['UseWetagQueriesHook'];
  prefetch: WetagPropDrivenHooks<TTag>['PrefetchFnWithGetter'];
  invalidate: WetagPropDrivenHooks<TTag>['InvalidateFnWithGetter'];
  getFromCache: WetagPropDrivenHooks<TTag>['GetFromCacheFnWithGetter'];
  fetchValue: WetagPropDrivenHooks<TTag>['FetchValueFnWithGetter'];

  useWetagBulk: WetagPropDrivenHooks<TTag>['UseWetagBulkHook'];
  useWetagBulkQuery: WetagPropDrivenHooks<TTag>['UseWetagBulkQueryHook'];
}

function resolveQueryClient(providedClient?: QueryClient): QueryClient {
  if (providedClient) return providedClient;
  return sharedQueryClient;
}

function createNamespacedWetagHooksWithGetter<TTag extends string>(
  namespace: string,
  useGetEntityId: () => string | undefined,
  getEntityId: () => string | undefined
): NamespacedHooksWithGetter<TTag> {
  const useNamespacedWetag: WetagHook<TTag> = (input, options = {}) => {
    const entityId = useGetEntityId();
    return useWetag(namespace, input, entityId, options);
  };

  const useNamespacedWetagQuery: WetagQueryHook<TTag> = (input, options = {}) => {
    const entityId = useGetEntityId();
    return useWetagQuery(namespace, input, entityId, options);
  };

  const usePrefetch: UsePrefetchHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>, options?: WetagQueryOptions) => {
        if (!entityId) return Promise.resolve();
        return prefetchWetag(queryClient, namespace, input, entityId, options);
      },
      [queryClient, entityId]
    );
  };

  const useInvalidate: UseInvalidateHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>) => {
        if (!entityId) return Promise.resolve();
        return invalidateWetag(queryClient, namespace, input, entityId);
      },
      [queryClient, entityId]
    );
  };

  const useGetFromCache: UseGetFromCacheHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>) => {
        if (!entityId) return undefined;
        return getWetagFromCache(queryClient, namespace, input, entityId);
      },
      [queryClient, entityId]
    );
  };

  const prefetchFn: PrefetchFnWithGetter<TTag> = (input, options = {}) => {
    const entityId = getEntityId();
    if (!entityId) return Promise.resolve();
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return prefetchWetag(queryClient, namespace, input, entityId, wetagOptions);
  };

  const invalidateFn: InvalidateFnWithGetter<TTag> = (input, options = {}) => {
    const entityId = getEntityId();
    if (!entityId) return Promise.resolve();
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return invalidateWetag(queryClient, namespace, input, entityId);
  };

  const getFromCacheFn: GetFromCacheFnWithGetter<TTag> = (input, options = {}) => {
    const entityId = getEntityId();
    if (!entityId) return undefined;
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return getWetagFromCache(queryClient, namespace, input, entityId);
  };

  const fetchValueFn: FetchValueFnWithGetter<TTag> = (input, options = {}) => {
    const entityId = getEntityId();
    if (!entityId) return Promise.resolve(false);
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return fetchWetagValue(queryClient, namespace, input, entityId, wetagOptions);
  };

  const useWetagQueriesHook: UseWetagQueriesHook<TTag> = (input, entityIds, options, strategy) =>
    useWetagQueries(namespace, input, entityIds, options, strategy);

  return {
    useWetag: useNamespacedWetag,
    useWetagQuery: useNamespacedWetagQuery,
    usePrefetch,
    useInvalidate,
    useGetFromCache,
    useWetagQueries: useWetagQueriesHook,
    prefetch: prefetchFn,
    invalidate: invalidateFn,
    getFromCache: getFromCacheFn,
    fetchValue: fetchValueFn,
  };
}

function createNamespacedWetagHooksWithoutGetter<TTag extends string>(
  namespace: string,
  useGetEntityId: () => string | undefined
): NamespacedHooks<TTag> {
  const useNamespacedWetag: WetagHook<TTag> = (input, options = {}) => {
    const entityId = useGetEntityId();
    return useWetag(namespace, input, entityId, options);
  };

  const useNamespacedWetagQuery: WetagQueryHook<TTag> = (input, options = {}) => {
    const entityId = useGetEntityId();
    return useWetagQuery(namespace, input, entityId, options);
  };

  const usePrefetch: UsePrefetchHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>, options?: WetagQueryOptions) => {
        if (!entityId) return Promise.resolve();
        return prefetchWetag(queryClient, namespace, input, entityId, options);
      },
      [queryClient, entityId]
    );
  };

  const useInvalidate: UseInvalidateHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>) => {
        if (!entityId) return Promise.resolve();
        return invalidateWetag(queryClient, namespace, input, entityId);
      },
      [queryClient, entityId]
    );
  };

  const useGetFromCache: UseGetFromCacheHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>) => {
        if (!entityId) return undefined;
        return getWetagFromCache(queryClient, namespace, input, entityId);
      },
      [queryClient, entityId]
    );
  };

  const useWetagQueriesHook: UseWetagQueriesHook<TTag> = (input, entityIds, options, strategy) =>
    useWetagQueries(namespace, input, entityIds, options, strategy);

  const prefetchFn: PrefetchFnWithEntityId<TTag> = (input, entityId, options = {}) => {
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return prefetchWetag(queryClient, namespace, input, entityId, wetagOptions);
  };

  const invalidateFn: InvalidateFnWithEntityId<TTag> = (input, entityId, options = {}) => {
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return invalidateWetag(queryClient, namespace, input, entityId);
  };

  const getFromCacheFn: GetFromCacheFnWithEntityId<TTag> = (input, entityId, options = {}) => {
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return getWetagFromCache(queryClient, namespace, input, entityId);
  };

  const fetchValueFn: FetchValueFnWithEntityId<TTag> = (input, entityId, options = {}) => {
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return fetchWetagValue(queryClient, namespace, input, entityId, wetagOptions);
  };

  return {
    useWetag: useNamespacedWetag,
    useWetagQuery: useNamespacedWetagQuery,
    usePrefetch,
    useInvalidate,
    useGetFromCache,
    useWetagQueries: useWetagQueriesHook,
    prefetch: prefetchFn,
    invalidate: invalidateFn,
    getFromCache: getFromCacheFn,
    fetchValue: fetchValueFn,
  };
}

export function createNamespacedWetagHooks<TTag extends string = string>(
  namespace: string,
  useGetEntityId: () => string | undefined,
  getEntityId: () => string | undefined
): NamespacedHooksWithGetter<TTag>;

export function createNamespacedWetagHooks<TTag extends string = string>(
  namespace: string,
  useGetEntityId: () => string | undefined
): NamespacedHooks<TTag>;

export function createNamespacedWetagHooks<TTag extends string = string>(
  namespace: string,
  useGetEntityId: () => string | undefined,
  getEntityId?: () => string | undefined
): NamespacedHooks<TTag> | NamespacedHooksWithGetter<TTag> {
  if (getEntityId) {
    return createNamespacedWetagHooksWithGetter<TTag>(namespace, useGetEntityId, getEntityId);
  }
  return createNamespacedWetagHooksWithoutGetter<TTag>(namespace, useGetEntityId);
}

export function createNamespacedWetagHooksForUser<TTag extends string = string>(
  namespace: string
): NamespacedHooksWithGetter<TTag> {
  return createNamespacedWetagHooksWithGetter<TTag>(namespace, currentUserId, currentUserId);
}

export function createNamespacedWetagHooksForOrg<TTag extends string = string>(
  namespace: string
): NamespacedHooksWithGetter<TTag> {
  return createNamespacedWetagHooksWithGetter<TTag>(namespace, useCurrentOrgId, getCurrentOrgId);
}

export function createNamespacedWetagHooksForLocation<TTag extends string = string>(
  namespace: string
): NamespacedHooksWithGetter<TTag> {
  return createNamespacedWetagHooksWithGetter<TTag>(namespace, useCurrentLocationId, getCurrentLocationId);
}

export function createWetagHooksForPropDriven<TTag extends string = string>(
  namespace: string
): NamespacedHooksForPropDriven<TTag> {
  const useNamespacedWetag: NamespacedHooksForPropDriven<TTag>['useWetag'] = (input, propEntityId, options) => {
    return useWetag(namespace, input, propEntityId, options);
  };

  const useNamespacedWetagBulk: NamespacedHooksForPropDriven<TTag>['useWetagBulk'] = (
    tag,
    inputs,
    strategy,
    options = {}
  ) => {
    return useWetagBulk(namespace, tag, inputs, strategy, options);
  };

  const useNamespacedWetagQuery: NamespacedHooksForPropDriven<TTag>['useWetagQuery'] = (
    input,
    propEntityId,
    options = {}
  ) => {
    return useWetagQuery(namespace, input, propEntityId, options);
  };

  const useNamespacedWetagBulkQuery: NamespacedHooksForPropDriven<TTag>['useWetagBulkQuery'] = (
    tag,
    inputs,
    strategy,
    options = {}
  ) => {
    return useWetagBulkQuery(namespace, tag, inputs, strategy, options);
  };

  const usePrefetch: NamespacedHooksForPropDriven<TTag>['usePrefetch'] = () => {
    const queryClient = useQueryClient();

    return useCallback(
      (input: WetagInput<TTag>, propEntityId, options?: WetagQueryOptions) => {
        if (!propEntityId) return Promise.resolve();
        return prefetchWetag(queryClient, namespace, input, propEntityId, options);
      },
      [queryClient]
    );
  };

  const useInvalidate: NamespacedHooksForPropDriven<TTag>['useInvalidate'] = () => {
    const queryClient = useQueryClient();

    return useCallback(
      (input: WetagInput<TTag>, propEntityId: string | undefined, options?: { namespace?: string }) => {
        if (!propEntityId) return Promise.resolve();
        return invalidateWetag(queryClient, options?.namespace ?? namespace, input, propEntityId);
      },
      [queryClient]
    );
  };

  const useGetFromCache: NamespacedHooksForPropDriven<TTag>['useGetFromCache'] = () => {
    const queryClient = useQueryClient();

    return useCallback(
      (input: WetagInput<TTag>, propEntityId: string | undefined, options?: { namespace?: string }) => {
        if (!propEntityId) return undefined;
        return getWetagFromCache(queryClient, options?.namespace ?? namespace, input, propEntityId);
      },
      [queryClient]
    );
  };

  const useWetagQueriesHook: NamespacedHooksForPropDriven<TTag>['useWetagQueries'] = (
    input,
    entityIds,
    options,
    strategy
  ) => {
    return useWetagQueries(namespace, input, entityIds, options, strategy);
  };

  const prefetchFn: NamespacedHooksForPropDriven<TTag>['prefetch'] = (input, propEntityId, options = {}) => {
    if (!propEntityId) return Promise.resolve();
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return prefetchWetag(queryClient, namespace, input, propEntityId, wetagOptions);
  };

  const invalidateFn: NamespacedHooksForPropDriven<TTag>['invalidate'] = (input, propEntityId, options = {}) => {
    if (!propEntityId) return Promise.resolve();
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return invalidateWetag(queryClient, namespace, input, propEntityId);
  };

  const getFromCacheFn: NamespacedHooksForPropDriven<TTag>['getFromCache'] = (input, propEntityId, options = {}) => {
    if (!propEntityId) return undefined;
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return getWetagFromCache(queryClient, namespace, input, propEntityId);
  };

  const fetchValueFn: NamespacedHooksForPropDriven<TTag>['fetchValue'] = (input, propEntityId, options = {}) => {
    if (!propEntityId) return Promise.resolve(false);
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return fetchWetagValue(queryClient, namespace, input, propEntityId, wetagOptions);
  };

  return {
    useWetag: useNamespacedWetag,
    useWetagQuery: useNamespacedWetagQuery,
    useWetagBulk: useNamespacedWetagBulk,
    useWetagBulkQuery: useNamespacedWetagBulkQuery,
    usePrefetch,
    useInvalidate,
    useGetFromCache,
    useWetagQueries: useWetagQueriesHook,
    prefetch: prefetchFn,
    invalidate: invalidateFn,
    getFromCache: getFromCacheFn,
    fetchValue: fetchValueFn,
  };
}
